import { useState, useRef, useCallback } from 'react';
import { useSession } from '../SessionContext';
import { StepShell } from './StepShell';
import { FeedbackDisplay } from './FeedbackDisplay';
import { PromptRenderer, type TiptapNode } from '@/shared/components/PromptRenderer';
import { LoadingIcon } from '@/interfaces/session-player/ui';
import { cn } from '@/foundation/utils/cn';
import type { ImageHotspotConfig } from '../types';

export function ImageHotspotStep() {
  const { state, dispatch, currentStep } = useSession();
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = snapshot?.config as ImageHotspotConfig | undefined;
  const media = currentStep?.mediaRefsJson as { url: string }[] | undefined;
  const imageUrl = media?.[0]?.url;

  const answer = state.answer;
  const clickX = answer?.type === 'image_hotspot' ? answer.x : null;
  const clickY = answer?.type === 'image_hotspot' ? answer.y : null;
  const result = state.result;
  const isGraded = state.phase === 'graded';
  const hotspotResult = result?.hotspotResult;

  // Use onPointerDown for consistent mouse + touch handling.
  // onTouchStart stopPropagation prevents useSwipeNavigation (window-level
  // touchstart/touchend listener) from triggering on hotspot interactions.
  // onPointerDown handles the actual click-to-select logic for both mouse and touch.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (isGraded) return;

      const img = imgRef.current;
      if (!img) return;

      const rect = img.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      dispatch({
        type: 'UPDATE_ANSWER',
        answer: {
          type: 'image_hotspot',
          x: Math.max(0, Math.min(1, x)),
          y: Math.max(0, Math.min(1, y)),
        },
      });
    },
    [dispatch, isGraded]
  );

  if (!imageUrl) {
    return (
      <StepShell>
        <div
          className="flex items-center justify-center py-12"
          style={{ color: 'var(--f-fg-muted)' }}
        >
          No image available for this step.
        </div>
      </StepShell>
    );
  }

  return (
    <StepShell>
      {/* Prompt text — no media passed since the image is the interaction surface below */}
      {snapshot?.prompt && (
        <PromptRenderer doc={snapshot.prompt as TiptapNode} media={[]} diagramJson={null} />
      )}

      <div className="flex flex-col items-center gap-4">
        {/* Hint */}
        {config?.targetCount && !isGraded && (
          <p className="text-sm" style={{ color: 'var(--f-fg-muted)' }}>
            Tap on the correct area{config.targetCount > 1 ? 's' : ''}
          </p>
        )}

        {/* Image with click/overlay */}
        <div
          className="relative w-full max-w-2xl cursor-crosshair select-none overflow-hidden rounded-xl touch-none"
          onPointerDown={handlePointerDown}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {!imgLoaded && (
            <div className="flex items-center justify-center py-12">
              <LoadingIcon size="md" />
            </div>
          )}
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Question image"
            className={cn('block w-full', !imgLoaded && 'invisible absolute inset-0')}
            onLoad={() => setImgLoaded(true)}
            draggable={false}
          />

          {imgLoaded && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
            >
              {/* Post-grading: show correct region(s) only */}
              {isGraded &&
                hotspotResult?.regions
                  .filter((region) => region.isCorrect)
                  .map((region) => (
                    <polygon
                      key={region.id}
                      points={region.points.map((p) => `${p.x},${p.y}`).join(' ')}
                      fill="color-mix(in srgb, var(--f-success) 20%, transparent)"
                      style={{ stroke: 'var(--f-success)' }}
                      strokeWidth={0.004}
                    />
                  ))}

              {/* Click marker (pre-grading: accent dot, post-grading: colored by result) */}
              {clickX !== null && clickY !== null && (
                <circle
                  cx={clickX}
                  cy={clickY}
                  r={0.015}
                  style={{
                    fill: isGraded
                      ? result?.isCorrect
                        ? 'var(--f-success)'
                        : 'var(--f-error)'
                      : 'var(--f-accent)',
                  }}
                  stroke="white"
                  strokeWidth={0.004}
                />
              )}
            </svg>
          )}
        </div>
      </div>

      {isGraded &&
        result &&
        (() => {
          let clickFeedback: string | null = null;
          if (hotspotResult?.clickedRegionId) {
            const region = hotspotResult.regions.find(
              (r) => r.id === hotspotResult.clickedRegionId
            );
            if (region) clickFeedback = `You clicked on the ${region.label}.`;
          } else if (hotspotResult) {
            clickFeedback = 'Your click did not land on a defined region.';
          }
          const combined = [clickFeedback, result.explanation].filter(Boolean).join(' ');
          return <FeedbackDisplay result={{ ...result, explanation: combined || null }} />;
        })()}
    </StepShell>
  );
}
